import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import NewsCard from "@/components/feature/NewsCard";
import Pagination from "@/components/base/Pagination";
import StatusBlock from "@/components/feature/StatusBlock";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import {
  categories,
  filterByRange,
  formatViews,
  getCategory,
  getNewsByCategory,
  paginate,
  relativeTime,
  sortedByHeat,
  sortedByTime,
} from "@/utils/news";

const RANGES = [
  { key: "all", label: "全部时间" },
  { key: "24h", label: "最近24小时" },
  { key: "7d", label: "最近7天" },
];

const SORTS = [
  { key: "time", label: "最新发布" },
  { key: "heat", label: "按热度" },
];

const PAGE_SIZE = 12;

export default function CategoryPage() {
  const { categoryName } = useParams();
  const navigate = useNavigate();
  const category = getCategory(categoryName);

  useDocumentMeta({
    title: `${category?.name ?? "分类"} - 爆料网`,
    description: `最新发布的${category?.name ?? "分类"}爆料，第一时间掌握热点资讯。${category?.desc ?? ""}`,
    keywords: `${category?.name ?? "爆料"},最新爆料,热点资讯,爆料网`,
  });

  const [range, setRange] = useState("all");
  const [sort, setSort] = useState("time");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);

  const source = useMemo(
    () => (categoryName ? getNewsByCategory(categoryName) : []),
    [categoryName],
  );

  const result = useMemo(() => {
    const ranged = filterByRange(source, range);
    return sort === "heat" ? sortedByHeat(ranged) : sortedByTime(ranged);
  }, [source, range, sort]);

  const paged = paginate(result, page, PAGE_SIZE);

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 320);
    return () => window.clearTimeout(timer);
  }, [range, sort, page, categoryName]);

  useEffect(() => {
    setPage(1);
  }, [range, sort, categoryName]);

  const resetFilters = () => {
    setRange("all");
    setSort("time");
    setPage(1);
  };

  const hotInCategory = useMemo(() => sortedByHeat(source).slice(0, 5), [source]);

  if (!category) {
    return (
      <PageLayout>
        <div className="mx-auto max-w-[1280px] px-4 md:px-6 py-16">
          <StatusBlock
            icon="ri-error-warning-line"
            title="没有找到这个分类"
            description="分类可能已经调整，你可以回到首页看看当前正在更新的爆料。"
            actionLabel="返回首页"
            onAction={() => navigate("/")}
          />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="mx-auto max-w-[1280px] px-4 md:px-6 pt-5 md:pt-7">
        <nav className="flex items-center gap-1.5 text-xs text-foreground-500 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <span>分类</span>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <span className="text-foreground-800">{category.name}</span>
        </nav>

        <header className="mt-4 rounded-lg border border-background-200/80 bg-background-100 px-5 md:px-7 py-6 md:py-7">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center text-xl">
                  <i className={category.slug === "society" ? "ri-government-line" : "ri-movie-2-line"} />
                </span>
                <div>
                  <p className="font-label text-[11px] tracking-[0.24em] text-foreground-400">
                    {category.en}
                  </p>
                  <h1 className="text-2xl md:text-[28px] font-black text-foreground-950 leading-tight">
                    {category.name}
                  </h1>
                </div>
              </div>
              <p className="mt-3 text-sm leading-7 text-foreground-600 max-w-[640px]">
                {category.desc}
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs text-foreground-500">
              <div>
                <p className="font-label text-2xl leading-none text-foreground-900">{source.length}</p>
                <p className="mt-1">条爆料</p>
              </div>
              <span className="h-8 w-px bg-background-300" />
              <div>
                <p className="font-label text-2xl leading-none text-foreground-900">
                  {formatViews(source.reduce((sum, item) => sum + item.views, 0))}
                </p>
                <p className="mt-1">累计阅读</p>
              </div>
            </div>
          </div>
        </header>

        <div className="mt-6 flex flex-col lg:flex-row gap-6 lg:gap-7 items-start">
          <div className="w-full lg:flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 pb-4 border-b border-background-200">
              <div className="hidden sm:flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
                {RANGES.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setRange(item.key)}
                    className={`px-3 py-1.5 rounded-full text-[13px] transition-colors cursor-pointer whitespace-nowrap ${
                      range === item.key
                        ? "bg-background-50 text-primary-600 font-bold"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setFilterOpen((v) => !v)}
                className="sm:hidden inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-background-300 text-sm text-foreground-700 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-filter-3-line" />
                筛选
                <i className={`ri-arrow-down-s-line transition-transform ${filterOpen ? "rotate-180" : ""}`} />
              </button>
              <div className="flex items-center gap-2">
                <span className="hidden md:inline text-xs text-foreground-400">
                  共 {result.length} 条结果
                </span>
                <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
                  {SORTS.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setSort(item.key)}
                      className={`px-3 py-1.5 rounded-full text-[13px] transition-colors cursor-pointer whitespace-nowrap ${
                        sort === item.key
                          ? "bg-background-50 text-primary-600 font-bold"
                          : "text-foreground-600 hover:text-primary-600"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {filterOpen && (
              <div className="sm:hidden mt-3 rounded-lg border border-background-200 bg-background-100 p-3 animate-fade-in">
                <p className="text-xs text-foreground-500 mb-2">时间范围</p>
                <div className="grid grid-cols-2 gap-2">
                  {RANGES.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setRange(item.key)}
                      className={`h-9 rounded-md text-[13px] cursor-pointer ${
                        range === item.key
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-50 text-foreground-700 border border-background-200"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-3 w-full h-9 rounded-md border border-background-300 text-[13px] text-foreground-700 cursor-pointer"
                >
                  重置筛选
                </button>
              </div>
            )}

            {loading ? (
              <div className="mt-5 flex flex-col items-center justify-center py-20">
                <span className="w-8 h-8 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin" />
                <p className="mt-3 text-sm text-foreground-500">正在加载爆料…</p>
              </div>
            ) : paged.items.length === 0 ? (
              <div className="mt-5">
                <StatusBlock
                  icon="ri-search-eye-line"
                  title="这个条件下暂时没有匹配的爆料"
                  description="换一个时间范围，或者重置筛选条件再看看。"
                  actionLabel="重置筛选"
                  onAction={resetFilters}
                  secondaryLabel="返回首页"
                  onSecondary={() => navigate("/")}
                />
              </div>
            ) : (
              <>
                <ul className="mt-5 space-y-3">
                  {paged.items.map((item) => (
                    <li key={item.id}>
                      <NewsCard item={item} />
                    </li>
                  ))}
                </ul>
                <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <p className="text-xs text-foreground-400">
                    第 {paged.page} / {paged.totalPages} 页 · 每页 {PAGE_SIZE} 条
                  </p>
                  <Pagination page={paged.page} totalPages={paged.totalPages} onChange={setPage} />
                </div>
              </>
            )}
          </div>

          <aside className="w-full lg:w-[320px] shrink-0 space-y-5">
            <section className="rounded-lg border border-background-200/80 bg-background-50 overflow-hidden">
              <div className="px-4 h-12 flex items-center justify-between border-b border-background-200/80">
                <h2 className="text-[15px] font-bold text-foreground-950 flex items-center gap-2">
                  <i className="ri-fire-line text-primary-500" />
                  {category.name}热榜
                </h2>
                <span className="font-label text-[11px] tracking-[0.18em] text-foreground-400">HOT</span>
              </div>
              <div className="px-4 py-1">
                {hotInCategory.map((item, index) => (
                  <Link
                    key={item.id}
                    to={`/detail/${item.id}`}
                    className="group flex items-start gap-3 py-3 border-b border-background-200/60 last:border-b-0 cursor-pointer"
                  >
                    <span
                      className={`w-5 shrink-0 font-label text-lg leading-none mt-0.5 ${
                        index < 3 ? "text-primary-500" : "text-foreground-300"
                      }`}
                    >
                      {`${index + 1}`.padStart(2, "0")}
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-[13px] leading-6 font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2-safe">
                        {item.title}
                      </h3>
                      <p className="mt-1 text-[11px] text-foreground-400">{relativeTime(item.publishedAt)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-background-200/80 bg-background-50 p-4">
              <h2 className="text-[15px] font-bold text-foreground-950">切换分类</h2>
              <div className="mt-3 space-y-2">
                {categories.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/category/${item.slug}`}
                    className={`flex items-center justify-between h-11 px-3 rounded-md text-sm transition-colors cursor-pointer ${
                      item.slug === category.slug
                        ? "bg-primary-50 text-primary-700 font-bold"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    {item.name}
                    <i className="ri-arrow-right-s-line" />
                  </Link>
                ))}
              </div>
            </section>
          </aside>
        </div>
      </div>
    </PageLayout>
  );
}